import { Autocomplete } from '@Wonder-Cave/ui';
import { IDropdownValue } from '@Wonder-Cave/ui/dist/Autocomplete';
import { axiosPost } from '@gr/portal/authAxios';
import useAreaCodes from '@gr/portal/hooks/useAreaCodes';
import {
  FilterDataTypeEnum,
  FilterOperatorEnum,
  IAreaCodeEntity,
  IHttpSearchResponse,
  ISearchFilter,
} from '@gr/shared/models';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { defaultAreaCodeOptions } from '../../../SystemNumbers/types';

interface IAreaCodeDropdownProps {
  value: IDropdownValue[] | undefined;
  onChange: (newValue?: (IDropdownValue | undefined)[]) => void;
  onBlur?: () => void;
  allClients?: boolean;
  disabled?: boolean;
  errorMessage?: string | undefined;
  showError: boolean;
  gridFilter?: boolean;
}

const AreaCodeDropdown = ({
  value,
  onChange,
  onBlur,
  disabled,
  errorMessage,
  showError,
}: IAreaCodeDropdownProps): JSX.Element => {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [initialLoad, setInitialLoad] = useState(false);
  const { data: areaCodeData, isPending: areaCodeLoading } = useAreaCodes(searchTerm);
  const areaCodeDropdowns: IDropdownValue[] =
    areaCodeData?.records.map((code) => ({
      label: `${code.areaCode} - ${code.stateText}`,
      value: code.id,
      additionalData: code.areaCode,
    })) ?? [];

  useEffect(() => {
    const findAreaCodes = async () => {
      setInitialLoad(true);
      try {
        const codes = value?.map((v) => v.additionalData);
        const filter: ISearchFilter = {
          dataType: FilterDataTypeEnum.NUMBER,
          fieldName: 'areaCode',
          operator: FilterOperatorEnum.IN,
          value: codes,
        };
        const currentACodes = await queryClient.fetchQuery({
          queryKey: ['area-codes', 'by-codes', codes],
          queryFn: async () => {
            const axios = await axiosPost<IHttpSearchResponse<IAreaCodeEntity>>('entities-search', {
              ...defaultAreaCodeOptions,
              filters: [...defaultAreaCodeOptions.filters, filter],
            });
            return axios.data?.data;
          },
          staleTime: 5 * 60 * 1000, // 5 minutes
          gcTime: 10 * 60 * 1000, // 10 minutes
        });
        onChange(
          currentACodes?.records?.map((code) => ({
            label: `${code.areaCode} - ${code.stateText}`,
            value: code.id,
            additionalData: code.areaCode,
          })) ?? []
        );
      } catch (e) {
        console.error(e);
      } finally {
        setInitialLoad(false);
      }
    };
    if (value && value?.length > 0) {
      findAreaCodes();
    }
  }, []);

  return (
    <Autocomplete
      label="Area Code"
      placeholder="Search area codes"
      value={initialLoad ? [] : value}
      options={areaCodeDropdowns}
      loading={areaCodeLoading}
      refreshOptions={async (searchValue) => setSearchTerm(searchValue)}
      onChange={onChange}
      onBlur={onBlur}
      error={errorMessage}
      disabled={disabled}
      multiple
      showError={showError}
      full
    />
  );
};

export default AreaCodeDropdown;
